import React, { useState } from 'react';
import { Layout, Dropdown, Button, Tooltip } from 'antd';
import { Link } from 'react-router-dom';
import {
  CaretDownOutlined,
  DesktopOutlined,
  GlobalOutlined,
  LockOutlined,
  MoonOutlined,
  SafetyOutlined,
  SunOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';

import MainMenu from '../Menu/Main';
import { hexToHexA } from '../utils/color';
import { useSelector } from 'react-redux';
import ChangePasswordModal from '../components/ChangePasswordModal';
import TwoFactorModal from '../components/TwoFactorModal';
import { tenantConfig } from '../config/tenantConfig';
import { buildLabel } from '../config/buildInfo';
import { setThemeMode } from '../utils/theme';
import useUiPrefs from '../hooks/useUiPrefs';
import './Header.scss';

const { Header } = Layout;

const MainHeader = () => {
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const [twoFactorOpen, setTwoFactorOpen] = useState(false);
  const user = useSelector((state) => state.auth.user);
  const { t, i18n } = useTranslation('common');
  // Level-3 restyle — colour theme toggle: light → dark → system → light.
  const { mode } = useUiPrefs();
  const nextThemeMode = mode === 'light' ? 'dark' : mode === 'dark' ? 'system' : 'light';
  const themeLabel = (m) =>
    m === 'dark' ? t('themeDark', 'Dark') : m === 'light' ? t('themeLight', 'Light') : t('themeSystem', 'System');
  const backgroundOpacity = hexToHexA(user?.primaryColor || '#93BFFF', 0.24);
  const avatarBackground = backgroundOpacity?.replace('#', '');
  const avatarColor = user?.primaryColor?.replace('#', '');

  const default_logo = tenantConfig.logoPath;

  const avatarUrl = `https://ui-avatars.com/api/?length=1&name=${
    user?.name || user?.email
  }&size=32&font-size=0.4&bold=true&background=${avatarBackground || '9863f3'}&color=${avatarColor}&rounded=true`;

  const dropdowns = [
    {
      label: i18n.language === 'fr' ? 'English' : 'Français',
      key: 'language',
      icon: <GlobalOutlined />,
      onClick: () => i18n.changeLanguage(i18n.language === 'fr' ? 'en' : 'fr'),
    },
    { type: 'divider' },
    {
      label: t('changePassword', 'Change Password'),
      key: 'change-password',
      icon: <LockOutlined />,
      onClick: () => setChangePasswordOpen(true),
    },
    {
      label: t('twoFactor', 'Two-factor authentication'),
      key: 'two-factor',
      icon: <SafetyOutlined />,
      onClick: () => setTwoFactorOpen(true),
    },
    { type: 'divider' },
    {
      label: (
        <Link to="/logout" className="test">
          <Button type="text" size="small">
            {t('logOut')}
          </Button>
        </Link>
      ),
      key: '/logout',
    },
    { type: 'divider' },
    {
      // #175 — the build this instance runs (same label on OVO and CT when they match).
      label: <span className="build-version">Version {buildLabel}</span>,
      key: 'build-version',
      disabled: true,
    },
  ];

  return (
    <>
      <Header className="MainHeader" style={{ position: 'fixed', zIndex: 9999, width: '100%' }}>
        <div className="logo">
          <Link to="/">
            <img src={default_logo} alt="logo" />
          </Link>
        </div>
        {user && (
          <>
            <MainMenu user={user} avatarUrl={avatarUrl} />
            <div className="desktop-account-wrapper">
              <Tooltip
                title={`${t('theme', 'Theme')}: ${themeLabel(mode)} · ${t('switchTo', 'switch to')} ${themeLabel(nextThemeMode)}`}
              >
                <Button
                  type="text"
                  className="theme-toggle"
                  aria-label="Toggle colour theme"
                  icon={mode === 'dark' ? <MoonOutlined /> : mode === 'light' ? <SunOutlined /> : <DesktopOutlined />}
                  onClick={() => setThemeMode(nextThemeMode)}
                />
              </Tooltip>
              <Dropdown menu={{ items: dropdowns }}>
                <div className="account-info">
                  <div className="avatar">
                    <img src={avatarUrl} alt="" />
                  </div>

                  <div className="info">
                    <div className="fullname">{user?.name}</div>

                    <div className="email">{user?.email}</div>
                  </div>
                  <CaretDownOutlined />
                </div>
              </Dropdown>
            </div>
          </>
        )}
      </Header>
      <ChangePasswordModal open={changePasswordOpen} onClose={() => setChangePasswordOpen(false)} />
      <TwoFactorModal open={twoFactorOpen} onClose={() => setTwoFactorOpen(false)} />
    </>
  );
};

export default MainHeader;
